import React from "react";
import { SlidersHorizontal, X } from "lucide-react";
import { DatePicker, Switch, TreeSelect } from "antd";
import CommonSelect from "../../../../core/common/commonSelect";
import PredefinedDateRangesOneMonth from "../../../../core/common/datePickerOneMonth";
import dayjs from "dayjs";

interface LeadsGridFiltersProps {
  showFilters: boolean;
  userRole: string;
  userChoose: any[];
  followUpOptions: any[];
  activityFilterOptions: any[];
  formDataFormChoose: { user_id: string; user_name: string };
  followUpFilter: string;
  customFollowUpDate: any;
  activityFilter: string;
  activityStartDate: any;
  activityEndDate: any;
  sortBy: string;
  selectedStartDate: any;
  selectedEndDate: any;
  searchQuery: string;
  tempSearchQuery: string;
  isActivityLoading: boolean;
  activeFilterCount: number;
  showOnlyTransferred: boolean;
  selectedLeadSource: any;
  leadSource: any[];

  setShowOnlyTransferred: (val: boolean) => void;
  handleLeadSourceChange: (val: string) => void;
  handleUserChange: (option: any) => void;
  setFollowUpFilter: (val: string) => void;
  setCustomFollowUpDate: (val: any) => void;
  handleActivityFilterChange: (option: any) => void;
  handleActivityDateChange: (start: any, end: any) => void;
  setSortBy: (val: string) => void;
  handleDateChange: (start: any, end: any) => void;
  setTempSearchQuery: (val: string) => void;
  handleSearchApply: () => void;
  handleResetFilters: () => void;

  pipelineFilterChoose?: any[];
  pipelineValue?: string;
  pipelineChar?: string;
  handlePipelineChange?: (option: any) => void;

  // Optional visibility flags – all default to true
  showSearch?: boolean;
  showAssignedTo?: boolean;
  showFollowUp?: boolean;
  showActivity?: boolean;
  showLeadSource?: boolean;
  showDateRange?: boolean;
  showTransferred?: boolean;
  showSortBy?: boolean;
  showPipeline?: boolean;
}

const LeadsGridFilters: React.FC<LeadsGridFiltersProps> = ({
  showFilters,
  userRole,
  userChoose,
  followUpOptions,
  activityFilterOptions,
  formDataFormChoose,
  followUpFilter,
  customFollowUpDate,
  activityFilter,
  activityStartDate,
  activityEndDate,
  sortBy,
  selectedStartDate,
  selectedEndDate,
  searchQuery,
  tempSearchQuery,
  isActivityLoading,
  activeFilterCount,
  showOnlyTransferred,
  selectedLeadSource,
  leadSource,

  pipelineFilterChoose,
  pipelineValue,
  pipelineChar,
  handlePipelineChange,

  setShowOnlyTransferred,
  handleLeadSourceChange,
  handleUserChange,
  setFollowUpFilter,
  setCustomFollowUpDate,
  handleActivityFilterChange,
  handleActivityDateChange,
  setSortBy,
  handleDateChange,
  setTempSearchQuery,
  handleSearchApply,
  handleResetFilters,

  showSearch = true,
  showAssignedTo = true,
  showFollowUp = true,
  showActivity = true,
  showLeadSource = true,
  showDateRange = true,
  showTransferred = true,
  showSortBy = true,
  showPipeline = false,
}) => {
  if (!showFilters) return null;

  return (
    <div className="">
      {/* Header */}
      <div className="lc-filter-header">
        <div className="lc-filter-icon">
          <SlidersHorizontal size={14} />
        </div>
        <h4 className="lc-filter-title">Filter by</h4>
      </div>

      <div className="lc-field-group">
        <div className="lc-fields-stack">

          {/* Search */}
          {showSearch && (
            <div>
              <label className="lc-label">Search</label>
              <div className="d-flex align-items-center gap-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search leads..."
                  value={tempSearchQuery}
                  onChange={(e) => setTempSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearchApply();
                    }
                  }}
                  style={{ height: '38px', borderRadius: '6px' }}
                />
                <button
                  className="btn btn-primary d-flex align-items-center justify-content-center"
                  style={{ height: '38px', width: '38px', borderRadius: '6px', padding: 0 }}
                  onClick={handleSearchApply}
                >
                  <i className="ti ti-search" />
                </button>
              </div>
            </div>
          )}

          {/* User Selection (Admin/EmployeeAdmin) */}
          {showAssignedTo && (userRole === "admin" || userRole === "employeeadmin") && (
            <div>
              <label className="lc-label">Assigned To</label>
              <CommonSelect
                options={userChoose}
                defaultValue={{ value: formDataFormChoose.user_id, label: formDataFormChoose.user_name }}
                onChange={handleUserChange}
              />
            </div>
          )}

          {/* Pipeline Stage */}
          {showPipeline && pipelineFilterChoose && handlePipelineChange && (
            <div>
              <label className="lc-label">Pipeline Stage</label>
              <CommonSelect
                options={pipelineFilterChoose}
                defaultValue={{ value: pipelineValue ?? "0", label: pipelineChar ?? "All Pipelines" }}
                onChange={handlePipelineChange}
              />
            </div>
          )}

          {/* Follow Up Filter */}
          {/* {showFollowUp && (
            <>
              <div>
                <label className="lc-label">Follow Up</label>
                <CommonSelect
                  options={followUpOptions}
                  defaultValue={followUpOptions.find(option => option.value === followUpFilter) || { label: 'Reminders', value: 'all' }}
                  onChange={(option: any) => setFollowUpFilter(option.value)}
                />
              </div>

              {/* Follow Up Date (Custom) */}
              {/* {followUpFilter === "custom" && (
                <div>
                  <label className="lc-label">Custom Follow Up Date</label>
                  <DatePicker
                    className="lc-full-width"
                    format="DD-MM-YYYY"
                    value={customFollowUpDate}
                    onChange={(date: any) => setCustomFollowUpDate(date)}
                    placeholder="Select date"
                    style={{ height: '38px', borderRadius: '6px' }}
                  />
                </div>
              )}
            </>
          )} */}

          {/* Activity Filter */}
          {showActivity && (
            <>
              <div>
                <label className="lc-label">Activity</label>
                <CommonSelect
                  options={activityFilterOptions}
                  defaultValue={activityFilterOptions.find(option => option.value === activityFilter) || activityFilterOptions[0]}
                  onChange={handleActivityFilterChange}
                />
                {isActivityLoading && (
                  <small className="text-primary mt-1 d-block">Loading activity data...</small>
                )}
              </div>

              {/* Activity Date Range (Custom) */}
              {activityFilter === "custom" && (
                <div>
                  <label className="lc-label">Custom Activity Date</label>
                  <PredefinedDateRangesOneMonth
                    startDate={activityStartDate}
                    endDate={activityEndDate}
                    onDateChange={handleActivityDateChange}
                  />
                </div>
              )}
            </>
          )}

          {/* Lead Source */}
          {showLeadSource && (
            <div>
              <label className="lc-label">Lead Source</label>
              <TreeSelect
                className="lc-full-width"
                value={selectedLeadSource}
                showSearch
                treeNodeFilterProp="title"
                filterTreeNode={(input, node) => {
                  if (!input || !node) return false;
                  return node.title?.toString().toLowerCase().includes(input.toLowerCase()) || false;
                }}
                dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
                treeData={leadSource}
                placeholder="Select Lead Source"
                allowClear
                treeDefaultExpandAll={false}
                onChange={handleLeadSourceChange}
                style={{ height: '38px' }}
              />
            </div>
          )}

          {/* Creation Date Range */}
          {showDateRange && (
            <div>
              <label className="lc-label">Lead Added Date Range</label>
              <PredefinedDateRangesOneMonth
                startDate={selectedStartDate}
                endDate={selectedEndDate}
                onDateChange={handleDateChange}
              />
            </div>
          )}

          {/* Transferred Switch */}
          {showTransferred && (
            <div className="d-flex align-items-center mb-2 mt-2">
              <Switch
                checked={showOnlyTransferred}
                onChange={setShowOnlyTransferred}
                checkedChildren="Transferred"
                unCheckedChildren="All Leads"
              />
            </div>
          )}

          {/* Sort By */}
          {showSortBy && (
            <div>
              <label className="lc-label">Sort By</label>
              <CommonSelect
                options={["Recently Added", "Ascending(Name)", "Descending(Name)", "Last Month", "Last 7 Days"].map(sort => ({ value: sort, label: sort }))}
                defaultValue={{ value: sortBy, label: sortBy }}
                onChange={(option: any) => setSortBy(option.value)}
              />
            </div>
          )}

        </div>
      </div>

      <div className="lc-fields-stack">
        <button
          onClick={handleResetFilters}
          className="lc-btn-secondary"
        >
          <X size={16} className="me-1" />
          Clear Filters {activeFilterCount > 0 ? `(${activeFilterCount})` : ''}
        </button>
      </div>

    </div>
  );
};

export default LeadsGridFilters;
